import { TrashIcon } from "@heroicons/react/24/solid";
import { Statuses } from "../../constants/statuses";
import { Roles } from "../../constants/roles";
import { useDispatch } from "react-redux";
import { setSelectedCase } from "../../redux/casesRedux";

const statusColor = (status) => {
  if (status === Statuses.READY) return "#4ADE80";
  if (status === Statuses.IN_PROGRESS) return "#FBBF24";
  if (status === Statuses.UPLOAD_DICOM) return "#FB923C";
  if (status === Statuses.DEMO) return "#60A5FA";
  return "#94A3B8";
};

const SortableTh = ({ label, sortKey, getSortDirection, requestSort, align = "left" }) => (
  <th
    scope="col"
    className={`px-4 py-3 cursor-pointer select-none text-${align} text-xs font-semibold uppercase tracking-wider`}
    style={{ color: "#94A3B8" }}
    onClick={() => requestSort(sortKey)}
  >
    <span className="inline-flex items-center gap-1">
      <span>{label}</span>
      <span style={{ color: "#30A69C" }}>{getSortDirection(sortKey)}</span>
    </span>
  </th>
);

const CaseListTable = ({
  caseList,
  requestSort,
  getSortDirection,
  showDeleteButtons,
  openHardDeleteModal,
  openSoftDeleteModal,
  navigate,
  user,
}) => {
  const dispatch = useDispatch();

  const isCreator = (caseItem) =>
    Object.entries(caseItem.users || {}).some(
      ([userId, userInfo]) => userId === user?.id && userInfo.role === Roles.CREATOR
    );

  const getSharedUsers = (caseItem) =>
    Object.values(caseItem.users || {})
      .filter((u) => u.role !== Roles.CREATOR)
      .map((u) => u.name)
      .join(", ") || "";

  const formatExpiration = (exp) => {
    if (!exp || exp === "Invalid Date") return "";
    return exp;
  };

  return (
    <div
      className="overflow-x-auto rounded-xl"
      style={{
        backgroundColor: "#FFFFFF",
        border: "1px solid #E2E8F0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr style={{ borderBottom: "1px solid #F1F5F9", backgroundColor: "#F8FAFC" }}>
            <SortableTh label="Title" sortKey="title" requestSort={requestSort} getSortDirection={getSortDirection} />
            <SortableTh label="Anatomy" sortKey="anatomy" requestSort={requestSort} getSortDirection={getSortDirection} />
            <SortableTh label="Creator" sortKey="creator" requestSort={requestSort} getSortDirection={getSortDirection} />
            <SortableTh label="Requester" sortKey="requester" requestSort={requestSort} getSortDirection={getSortDirection} />
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: "#64748B" }}>Shared</th>
            <SortableTh label="Created" sortKey="creationDate" requestSort={requestSort} getSortDirection={getSortDirection} />
            <SortableTh label="Due" sortKey="dueDate" requestSort={requestSort} getSortDirection={getSortDirection} />
            <SortableTh label="Expires" sortKey="expirationDate" requestSort={requestSort} getSortDirection={getSortDirection} />
            <SortableTh label="Status" sortKey="status" requestSort={requestSort} getSortDirection={getSortDirection} />
            {showDeleteButtons && (
              <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: "#64748B" }}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {caseList.length === 0 && (
            <tr>
              <td colSpan={showDeleteButtons ? 10 : 9} className="text-center py-10 text-sm" style={{ color: "#64748B" }}>
                No cases found.
              </td>
            </tr>
          )}
          {caseList.map((caseItem, idx) => (
            <tr
              key={caseItem.id}
              className="cursor-pointer transition-colors duration-150"
              style={{
                borderBottom: idx < caseList.length - 1 ? "1px solid #F1F5F9" : "none",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#F8FAFC"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
              onClick={() => {
                dispatch(setSelectedCase(caseItem));
                navigate(`/case/${caseItem.id}`);
              }}
            >
              <td className="px-4 py-3 font-semibold" style={{ color: "#0F172A" }}>
                {caseItem.title}
                {caseItem.isAdminView && (
                  <span
                    className="ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ backgroundColor: "rgba(139,92,246,0.2)", color: "#A78BFA" }}
                  >
                    Admin
                  </span>
                )}
              </td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{caseItem.anatomy}</td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{caseItem.creator}</td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{caseItem.requester}</td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{!caseItem.isDemo ? getSharedUsers(caseItem) : ""}</td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{caseItem.creationDate}</td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{caseItem.dueDate}</td>
              <td className="px-4 py-3" style={{ color: "#475569" }}>{formatExpiration(caseItem.expirationDate)}</td>
              <td className="px-4 py-3 font-bold" style={{ color: statusColor(caseItem.status) }}>
                {caseItem.status}
              </td>
              {showDeleteButtons && (
                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => isCreator(caseItem) ? openHardDeleteModal(caseItem.id) : openSoftDeleteModal(caseItem.id)}
                    className="p-1.5 rounded-full transition-all duration-150"
                    style={{
                      backgroundColor: isCreator(caseItem) ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)",
                      color: isCreator(caseItem) ? "#F87171" : "#4ADE80",
                    }}
                    title={isCreator(caseItem) ? "Delete for everyone" : "Remove from my list"}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CaseListTable;
